// @ts-nocheck
import { useSelector } from 'react-redux';
import { Box, Dialog as MDialog, DialogContent, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { FHButton, FHMUIDataTable } from '../../../base';
import { FORTA_BLUE, FORTA_GRAY4, FORTA_WHITE } from '../../../../utils/colors';
import { ClaimSessionTableColumns } from './TableConfig';
import { ClaimSessionTableOptions } from './TableRenderOptions';
import { Themes } from '../../../../constants';
import { getThemeColor } from '../../../../utils/common';
import CloseIcon from '../../../svgs/CloseIcon';
import { AllowedServiceCodesToBuildClaim } from './constants';

import type { RootState } from '../../../../store';
import type { DialogProps as MDialogProps } from '@mui/material';
import type { IClientPolicyMap, ISessionWithClaimStatus } from '../../../../utils/types';

interface ClaimReviewDialogProps {
  show: boolean;
  title: string;
  sessions: ISessionWithClaimStatus[];
  authorizer: string;
  providerName: string;
  displayClient: string;
  totalNoOfSessions: number;
  totalDurationOfSessions: number;
  selectedProcedureCode: string;
  selectedPolicyDetails?: IClientPolicyMap;
  onClose: () => void;
  onSubmit: () => void;
  historyClaimReviewDialogView?: boolean;
  hideDialogFooter?: boolean;
  colorTheme?: Themes;
  submitButtonStyles?: any;
  dialogStyles?: any;
  headerStyles?: any;
  titleStyles?: any;
  footerContainerStyles?: any;
  submitBtnTestId?: string;
}

interface ClaimDialogProps extends MDialogProps {
  dialogStyles?: any;
}
const StyledDialog = styled(MDialog, {
  shouldForwardProp: (prop) => prop !== 'colorTheme' && prop !== 'dialogStyles',
})<ClaimDialogProps>(({ dialogStyles }: any) => ({
  '& .MuiDialog-paper': {
    padding: '16px 24px',
    height: '760px',
    width: '80vw',
    maxHeight: '760px',
    maxWidth: '80vw',
    backgroundColor: FORTA_WHITE,
    backgroundSize: '100%',
    boxShadow: `8px 8px 8px 0px rgba(74, 74, 74, 0.25)`,
    borderRadius: '8px',
    margin: 0,
    border: `2px solid ${FORTA_BLUE.light3}`,
    overflowY: 'auto',
    ...dialogStyles,
  },
  '& .MuiDialogContent-root': {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    overflowY: 'unset',
  },
}));
function ClaimReviewDialog(props: ClaimReviewDialogProps) {
  const {
    show,
    title,
    sessions,
    authorizer,
    providerName,
    displayClient,
    totalNoOfSessions,
    totalDurationOfSessions,
    selectedProcedureCode,
    selectedPolicyDetails,
    onClose,
    onSubmit,
    historyClaimReviewDialogView = false,
    hideDialogFooter,
    dialogStyles = {},
    colorTheme = Themes.Primary,
    ...rest
  } = props;
  const { loading: claimsLoading } = useSelector((state: RootState) => state.claims);

  const renderMUIDataTable = () => {
    return (
      <FHMUIDataTable
        containerStyles={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          margin: '2px',
          padding: '16px',
        }}
        title=""
        data={sessions.filter((session: ISessionWithClaimStatus) => session.selected)}
        columns={ClaimSessionTableColumns(
          false,
          false,
          () => {},
          historyClaimReviewDialogView,
          selectedProcedureCode === AllowedServiceCodesToBuildClaim.SessionProcedureCode,
        )}
        options={ClaimSessionTableOptions(
          false,
          selectedProcedureCode,
          () => {},
          historyClaimReviewDialogView,
          selectedPolicyDetails,
        )}
      />
    );
  };

  const DialogHeader = () => (
    <Box
      data-testid="claimReviewDialogHeader"
      sx={{
        display: 'flex',
        gap: 3,
        flexDirection: 'column',
        p: 2,
      }}
    >
      {!historyClaimReviewDialogView && (
        <Typography fontSize={'14px'} lineHeight={'21px'} fontWeight={400} align="left" my={3}>
          Please ensure all sessions included are accurate for this claim, including the date range
          and the BCBA name listed. Once a claim is built, it cannot be deleted.
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          pb: 3,
          gap: historyClaimReviewDialogView ? 3 : 6,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: '700',
              textAlign: 'left',
              color: FORTA_BLUE.primary,
            }}
          >
            Client
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: '700',
              textAlign: 'left',
              color: FORTA_GRAY4,
            }}
          >
            {displayClient}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: '700',
              textAlign: 'left',
              color: FORTA_BLUE.primary,
            }}
          >
            BCBA
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: '700',
              textAlign: 'left',
              color: FORTA_GRAY4,
            }}
          >
            {providerName}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: '700',
              textAlign: 'left',
              color: FORTA_BLUE.primary,
            }}
          >
            Authorizer
          </Typography>
          <Typography
            sx={{ fontSize: '14px', fontWeight: '700', textAlign: 'left', color: FORTA_GRAY4 }}
          >
            {authorizer}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: '700',
              textAlign: 'left',
              color: FORTA_BLUE.primary,
            }}
          >
            Total sessions
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: '700',
              textAlign: 'left',
              color: FORTA_GRAY4,
            }}
          >
            {totalNoOfSessions}
          </Typography>
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: '700',
              textAlign: 'left',
              color: FORTA_BLUE.primary,
            }}
          >
            Total duration
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: '700',
              textAlign: 'left',
              color: FORTA_GRAY4,
            }}
          >
            {totalDurationOfSessions}
            {' hours'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <StyledDialog {...rest} open={show} dialogStyles={dialogStyles}>
      <DialogContent>
        <IconButton
          sx={{ p: 0, position: 'absolute', top: 32, right: 24 }}
          disableRipple
          onClick={onClose}
        >
          <CloseIcon color={getThemeColor(colorTheme).primary} />
        </IconButton>
        {title && (
          <Typography variant="h4" fontWeight={500} align="left" sx={{ color: FORTA_BLUE.primary }}>
            {title}
          </Typography>
        )}
        {DialogHeader()}
        {renderMUIDataTable()}
        {!hideDialogFooter && (
          <Box sx={{ display: 'flex', pb: 1, justifyContent: 'space-between', pt: 3 }}>
            <FHButton
              colorTheme={colorTheme}
              onClick={onClose}
              lightBackground
              sx={{
                backgroundColor: FORTA_WHITE,
                border: `1px solid ${getThemeColor(colorTheme).light2}`,
                color: FORTA_GRAY4,
                mr: 3,
                '&:hover': {
                  backgroundColor: FORTA_WHITE,
                  border: `1px solid ${getThemeColor(colorTheme).light2}`,
                  color: FORTA_GRAY4,
                },
              }}
            >
              {'Cancel'}
            </FHButton>

            <FHButton
              data-testid={'submitGenerateClaimId'}
              sx={{
                fontSize: '21px',
                lineHeight: '38px',
                fontWeight: '700',
                padding: '8px 16px',
              }}
              colorTheme={colorTheme}
              onClick={onSubmit}
              disabled={claimsLoading}
            >
              {'Generate claim'}
            </FHButton>
          </Box>
        )}
      </DialogContent>
    </StyledDialog>
  );
}

export default ClaimReviewDialog;
